import React, { useEffect, useState } from 'react';
import { useStoreActions, useStoreState } from 'easy-peasy';
import { useParams } from 'react-router-dom';
import SecondaryLayout from '@app/layouts/SecondaryLayout';
import ProfileInfo from '@components/ProfileInfo';
import Horsejog from '@components/Horsejog';
import Modal from '@components/Modal';
import Masthead from '@components/Masthead';
import CandidateDetailLoader from '@app/loaders/CandidateDetail';
import useParsedCandidateDetails from '@utils/hooks/useParsedCandidateDetails';
import { Skills } from '@components/candidate-detail/skills/index';
import { ShareLinkModal } from '@components/ShareLinkModal/index';
import { MODAL_IDS_MAP } from '@app/constants/modals';
import { utilityThrowError } from '@utils/errors';
import { ApplicationFeedback } from '@components/ApplicationFeedback/index';
import { GTM } from '@app/gtm';
import { ScoutReport } from '@components/ScoutReport/index';
import { CandidateDossier } from '@components/CandidateDossier/index';
import { NegativeFeedbackModal } from '@components/NegativeFeedbackModal/NegativeFeedbackModal';
import {
  jobCompanyNameSelector,
  jobStoreSelector,
  selectorJobIsReverseMarketing,
} from '@model/Job/Job.selectors';
import {
  candidateDetailLoadingSelector,
  candidateSelector,
  getCandidateSelector,
  getCurriculumSelector,
  putClientFeedbackSelector,
} from '@model/Candidate/Candidate.selectors';
import { Chat } from '@components/Molecules/Chat/index';

const CandidateDetail = () => {
  const { candidateId } = useParams();
  const [manager, setManager] = useState(null);

  // STATE
  const job = useStoreState(jobStoreSelector);
  const companyName = useStoreState(jobCompanyNameSelector);
  const candidateDetailLoading = useStoreState(candidateDetailLoadingSelector);
  const candidateError = useStoreState(
    state => state.candidates.candidateError,
  );
  const candidate = useStoreState(candidateSelector);
  const isModalOpened = useStoreState(state => state.isModalOpened);
  const modalId = useStoreState(state => state.modalId);
  const isJobReverseMarketing = useStoreState(selectorJobIsReverseMarketing);

  const {
    candidateFullName,
    candidateProfileInfo,
    candidateHorse,
    customerInterest,
    isCandidateInterviewed,
  } = useParsedCandidateDetails(candidate, candidateDetailLoading);

  const isScoutReportAvailable = candidate
    ? Boolean(candidate.technicalReport)
    : false;

  const putClientFeedback = useStoreActions(putClientFeedbackSelector);
  const getCandidate = useStoreActions(getCandidateSelector);
  const getCurriculum = useStoreActions(getCurriculumSelector);

  const setModalOpened = useStoreActions(actions => actions.setModalOpened);
  const setModalId = useStoreActions(actions => actions.setModalId);

  const isShareModalVisible = isModalOpened && modalId === MODAL_IDS_MAP.SHARE;

  const isNegativeFeedbackModalVisible =
    isModalOpened && modalId === MODAL_IDS_MAP.NEGATIVE_FEEDBACK;

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  }, []);

  useEffect(() => {
    if (job?.id && candidateId) {
      getCandidate({ candidateId, jobId: job?.id });
      getCurriculum({ candidateId, jobId: job?.id });
    }
  }, [candidateId, getCandidate, getCurriculum, job?.id]);

  useEffect(() => {
    if (job) {
      setManager(job?.recruitmentConsultant ?? job?.salesConsultant);
    }
  }, [job?.recruitmentConsultant, job?.salesConsultant]);

  useEffect(() => {
    const handlePrintEvents = () => {
      const accordions = document.querySelectorAll('.c-accordion__label');
      if (accordions) {
        Array.from(accordions).forEach(acc => acc.click());
      }
    };
    window.addEventListener('beforeprint', handlePrintEvents);
    window.addEventListener('afterprint', handlePrintEvents);

    return () => {
      window.removeEventListener('beforeprint', handlePrintEvents);
      window.removeEventListener('afterprint', handlePrintEvents);
    };
  });

  const getMastheadClassModifier = isCustomerInterested => {
    if (candidate?.discardedAt) {
      return 'waiting';
    }

    if (isCandidateInterviewed) {
      return 'positive';
    }

    if (isCustomerInterested === undefined || isCustomerInterested === null) {
      return 'waiting';
    }

    return isCustomerInterested ? 'positive' : 'negative';
  };

  const handleAnalytics = interest => {
    const feedback = interest ? 'Promosso' : 'Bocciato';

    GTM.trackGTMEvent({
      action: EVENT_ACTION_FEEDBACK_UPDATE,
      label: `${companyName} - ${job.id} - ${job.title} - ${feedback}`,
    });

    GTM.trackGA4Event(
      EVENT_ACTION_FEEDBACK_UPDATE,
      companyName,
      `${job.id} - ${job.title} - ${feedback}`,
    );
  };

  const openNegativeFeedbackModal = () => {
    setModalOpened(true);
    setModalId(MODAL_IDS_MAP.NEGATIVE_FEEDBACK);
  };

  const handleOnInterestFeedback = async (
    isCustomerInterested,
    customerInterestNotes,
  ) => {
    if (isCustomerInterested === false && !customerInterestNotes) {
      return openNegativeFeedbackModal();
    }
    const clientFeedbackPayload = {
      jobId: job.id,
      candidateId,
      notes: customerInterestNotes,
    };

    await putClientFeedback(clientFeedbackPayload);
    handleAnalytics(isCustomerInterested);
    await getCandidate({ candidateId, jobId: job.id });
  };

  const onFeedbackUpdate = async interested => {
    try {
      await handleOnInterestFeedback(interested);
    } catch (error) {
      console.warn(error);
    }
  };

  useEffect(() => {
    if (!!candidateError) {
      utilityThrowError(candidateError, 'Candidate page failed to render');
    }
  }, [candidateError]);

  return (
    <SecondaryLayout>
      {candidateDetailLoading || !job ? (
        <div>
          <CandidateDetailLoader />
        </div>
      ) : (
        <div data-testid="candidate-page">
          <Masthead
            isBackToJobAvailable
            modifier={`c-app-masthead--candidate-${getMastheadClassModifier(
              customerInterest,
            )}`}
            job={job}
          >
            {{
              right: (
                <ApplicationFeedback
                  candidateFullName={candidateFullName}
                  isCustomerInterested={customerInterest}
                  isCandidateDiscarded={candidate?.discardedAt}
                  isCandidateInterviewed={isCandidateInterviewed}
                  onFeedbackUpdate={onFeedbackUpdate}
                  manager={manager}
                />
              ),
            }}
          </Masthead>

          {candidateHorse ? (
            <div className="rev-o-container rev-o-container--full">
              <div className="rev-o-grid">
                <div
                  data-testid="candidate-page-horsejog"
                  className="rev-o-col-12"
                >
                  <Horsejog horse={candidateHorse} showLinks={false} />
                </div>
              </div>
            </div>
          ) : null}

          <div className="rev-o-container candidate-profile-info-chat-wrapper">
            <div className="rev-o-grid">
              <div
                data-testid="candidate-page-details"
                className="rev-u-px-16 rev-o-col-12 rev-o-col--medium-6 rev-u-mt-64"
              >
                <ProfileInfo {...candidateProfileInfo} />
              </div>
              <div className="rev-u-px-16 rev-o-col-12 rev-o-col--medium-6 rev-u-mt-64">
                <Chat />
              </div>
            </div>
          </div>

          {isJobReverseMarketing ? null : (
            <Skills
              candidateSkills={candidate?.skills}
              isScoutReportAvailable={isScoutReportAvailable}
            />
          )}

          {isScoutReportAvailable && (
            <ScoutReport scoutReport={candidate?.technicalReport} />
          )}

          <CandidateDossier candidateSummary={candidate?.summary} />
        </div>
      )}

      <Modal visible={isShareModalVisible}>
        <ShareLinkModal />
      </Modal>

      <Modal
        visible={isNegativeFeedbackModalVisible}
        modifier="c-app-modal--negative-feedback"
      >
        <NegativeFeedbackModal
          onSendNegativeFeedback={async notes =>
            await handleOnInterestFeedback(false, notes)
          }
        />
      </Modal>
    </SecondaryLayout>
  );
};

export default CandidateDetail;

const EVENT_ACTION_FEEDBACK_UPDATE = 'promossobocciato';
